/**
 * QueryBuilder canvas ↔ engine-spec core (ADR-0063 P5 F1) — PURE, unit-tested.
 *
 * The visual QueryBuilder is a projection of the SAME spec P5 persists on a
 * Dataset's `config.model.joins`. This module is the single source of the
 * spec↔graph mapping: it turns the persisted joins into ReactFlow-shaped nodes +
 * edges (one edge per ON condition, drawn column→column), and folds a drag-connect
 * back into an ON pair. No React, no ReactFlow import — just the model, so it can
 * be reasoned about + tested in isolation (the canvas is the deferred test tier).
 *
 * The join model (mirrors the backend engine `_from_clause`): a join
 * `{dataset, type, on:[{left_field, right_field, left_dataset?}]}` reads
 * `left_field` on `left_dataset` (the PRIMARY when omitted, or an EARLIER join) =
 * `right_field` on the join's OWN target. So an ON's LEFT side must reference a
 * relation that appears BEFORE the join — the same idx rule the engine enforces.
 */
import type {
  DatasetModelJoin,
  DatasetModelSpec,
  DatasetModelUnfold,
  DatasetModelUnion,
} from "@/lib/api-datasets";

/** The primary relation's node id (it isn't a join, so it has no name key). */
export const PRIMARY_ID = "__primary__";

export interface QbNode {
  id: string; //: PRIMARY_ID, or the join's `dataset` name (unique per query)
  kind: "primary" | "dataset" | "entity";
  label: string;
  columns: string[];
  /** null for the primary; else the index into `joins`. */
  joinIndex: number | null;
  type?: "inner" | "left";
}

export interface QbEdge {
  id: string; //: `on:<joinIndex>:<onIndex>`
  source: string; //: left node id (left_dataset, or PRIMARY_ID)
  sourceCol: string; //: left_field
  target: string; //: right node id (the join's target)
  targetCol: string; //: right_field
  joinIndex: number;
  onIndex: number;
}

/** One node for the primary + one per join, given resolved columns per node id. */
export function buildNodes(
  primaryLabel: string,
  joins: DatasetModelJoin[],
  columnsById: Record<string, string[]>,
): QbNode[] {
  const nodes: QbNode[] = [
    {
      id: PRIMARY_ID,
      kind: "primary",
      label: primaryLabel,
      columns: columnsById[PRIMARY_ID] ?? [],
      joinIndex: null,
    },
  ];
  joins.forEach((j, i) => {
    nodes.push({
      id: j.dataset,
      kind: j.source === "entity" ? "entity" : "dataset",
      label: j.dataset,
      columns: columnsById[j.dataset] ?? [],
      joinIndex: i,
      type: j.type,
    });
  });
  return nodes;
}

/** One edge per ON pair — the left node's `left_field` → the join target's
 *  `right_field`. `left_dataset` omitted ⇒ the primary. */
export function buildEdges(joins: DatasetModelJoin[]): QbEdge[] {
  const edges: QbEdge[] = [];
  joins.forEach((j, ji) => {
    (j.on ?? []).forEach((o, oi) => {
      const leftName = (o.left_dataset ?? "").trim();
      edges.push({
        id: `on:${ji}:${oi}`,
        source: leftName || PRIMARY_ID,
        sourceCol: o.left_field,
        target: j.dataset,
        targetCol: o.right_field,
        joinIndex: ji,
        onIndex: oi,
      });
    });
  });
  return edges;
}

/** The index of a node in the join order: -1 for the primary (it's before every
 *  join), else its position in `joins`, or `null` if it isn't in the query. */
function orderIndex(joins: DatasetModelJoin[], nodeId: string): number | null {
  if (nodeId === PRIMARY_ID) return -1;
  const i = joins.findIndex((j) => j.dataset === nodeId);
  return i < 0 ? null : i;
}

export interface QbConnect {
  sourceNode: string;
  sourceCol: string;
  targetNode: string;
  targetCol: string;
}

/**
 * Fold a drag-connect into a new ON pair on the RIGHT join (ADR-0063 P5 F1). The
 * "right" side of an ON is always the LATER relation (higher join index); the
 * "left" is the earlier one (a lower index, or the primary). So we compare the two
 * connected nodes' order and add `{left_field, right_field, left_dataset?}` to the
 * later one's join. Returns the updated joins, or `null` when the connect is
 * invalid (same node, two primaries, or a node not in the query) — the caller
 * ignores a null (no-op), never crashes.
 */
export function connectToJoins(
  joins: DatasetModelJoin[],
  conn: QbConnect,
): DatasetModelJoin[] | null {
  const a = orderIndex(joins, conn.sourceNode);
  const b = orderIndex(joins, conn.targetNode);
  if (a === null || b === null || a === b) return null; // unknown node or self-join
  const sourceIsRight = a > b; // the higher index is the join being ON-ed
  const rightIdx = sourceIsRight ? a : b;
  if (rightIdx < 0) return null; // both sides are the primary → no join to attach to
  const rightCol = sourceIsRight ? conn.sourceCol : conn.targetCol;
  const leftNode = sourceIsRight ? conn.targetNode : conn.sourceNode;
  const leftCol = sourceIsRight ? conn.targetCol : conn.sourceCol;
  const pair = {
    left_field: leftCol,
    right_field: rightCol,
    ...(leftNode !== PRIMARY_ID ? { left_dataset: leftNode } : {}),
  };
  return joins.map((j, i) => (i === rightIdx ? { ...j, on: [...(j.on ?? []), pair] } : j));
}

/** Remove one ON pair (an edge) — if it was the join's LAST condition, the join
 *  itself is left with an empty `on` (the node stays; the operator can re-draw or
 *  remove the target). */
export function removeOn(
  joins: DatasetModelJoin[],
  joinIndex: number,
  onIndex: number,
): DatasetModelJoin[] {
  return joins.map((j, i) =>
    i === joinIndex ? { ...j, on: (j.on ?? []).filter((_, k) => k !== onIndex) } : j,
  );
}

/** Flip a join's type between inner and left. */
export function setJoinType(
  joins: DatasetModelJoin[],
  joinIndex: number,
  type: "inner" | "left",
): DatasetModelJoin[] {
  return joins.map((j, i) => (i === joinIndex ? { ...j, type } : j));
}

// --- The full query SHAPE the QueryBuilder panels author (F2–F5) ----------------

/** A projected/grouped field ref — `dataset` omitted ⇒ the primary. */
export interface QbFieldRef {
  dataset?: string;
  field: string;
  as?: string;
  bucket?: "day" | "week" | "month" | "quarter" | "year"; // group_by only (time series)
}
export interface QbAgg {
  fn: string; //: count · sum · avg · min · max
  field?: string; // omitted for count(*)
  as?: string;
}
export interface QbFilter {
  dataset?: string;
  field: string;
  op: string;
  value?: unknown;
}
export interface QbSort {
  by: string; // a SELECTED/group/agg alias
  dir: "asc" | "desc";
}

/** The whole persisted query shape (the QueryBuilder's output), all optional. */
export interface QbShape {
  select: QbFieldRef[];
  group_by: QbFieldRef[];
  aggregations: QbAgg[];
  filters: QbFilter[];
  match: "all" | "any";
  order_by: QbSort[];
  search: string;
}

/**
 * Assemble the `config.model` object from the joins + the authored shape,
 * DROPPING every empty piece so a plain dataset stays a plain read (ADR-0063 P5
 * F2–F5). Returns `{}` when nothing is set (which CLEARS the federation server-side).
 * `match`/`bucket` ride only when their owning field is present. This is the single
 * spec↔panels seam — pure + unit-tested so the persisted shape can't drift.
 */
export function buildModelSpec(
  joins: DatasetModelJoin[],
  shape: QbShape,
  unions: DatasetModelUnion[] = [],
  unfold: DatasetModelUnfold[] = [],
): DatasetModelSpec | Record<string, never> {
  const fed: Record<string, unknown> = {};
  if (joins.length) fed.joins = joins;
  // UNION branches (ADR-0064 P5) — drop an INCOMPLETE branch (no mapped column) so an
  // in-progress authoring never persists a dead union the backend would reject.
  const readyUnions = unions
    .map((u) => ({ ...u, columns: u.columns.filter((c) => c.output && c.field) }))
    .filter((u) => u.dataset && u.columns.length);
  if (readyUnions.length) fed.unions = readyUnions;
  // UNFOLD columns (ADR-0064 P5 Transformation) — drop an entry with no field so an
  // in-progress row never persists a dead unfold.
  const readyUnfold = unfold.filter((u) => u.field.trim());
  if (readyUnfold.length) fed.unfold = readyUnfold;
  if (shape.group_by.length) fed.group_by = shape.group_by;
  if (shape.aggregations.length) fed.aggregations = shape.aggregations;
  // A plain projection only applies when NOT grouping (the engine ignores `select`
  // when grouping) — mirror that here so we never persist a dead `select`.
  if (shape.select.length && !shape.group_by.length && !shape.aggregations.length) {
    fed.select = shape.select;
  }
  if (shape.filters.length) {
    fed.filters = shape.filters;
    if (shape.match === "any") fed.match = "any";
  }
  if (shape.order_by.length) fed.order_by = shape.order_by;
  if (shape.search.trim()) fed.search = shape.search.trim();
  return Object.keys(fed).length ? (fed as unknown as DatasetModelSpec) : {};
}

/** Read the persisted UNION branches back off a `config.model` object (the inverse of
 *  the `unions` piece of {@link buildModelSpec}). */
export function unionsFromModel(fed: Record<string, unknown> | undefined): DatasetModelUnion[] {
  const u = (fed ?? {})["unions"];
  return Array.isArray(u) ? (u as DatasetModelUnion[]) : [];
}

/** Append a union branch (empty column map) for a target dataset/entity, de-duping by
 *  name — mirrors the join add. `union_all` is the default (keep all rows). */
export function addUnion(
  unions: DatasetModelUnion[],
  target: string,
  opts: { source?: "entity"; app_id?: string } = {},
): DatasetModelUnion[] {
  const name = target.trim();
  if (!name || unions.some((u) => u.dataset === name)) return unions;
  return [
    ...unions,
    { dataset: name, type: "union_all", columns: [], ...(opts.source ? { source: opts.source } : {}), ...(opts.app_id ? { app_id: opts.app_id } : {}) },
  ];
}

/** Remove a whole union branch by index. */
export function removeUnion(unions: DatasetModelUnion[], idx: number): DatasetModelUnion[] {
  return unions.filter((_, i) => i !== idx);
}

/** Flip a branch between `union` (dedup) and `union_all` (keep all). */
export function setUnionType(
  unions: DatasetModelUnion[],
  idx: number,
  type: "union" | "union_all",
): DatasetModelUnion[] {
  return unions.map((u, i) => (i === idx ? { ...u, type } : u));
}

/** Map (or, with `field=""`, CLEAR) the branch column that supplies one OUTPUT column.
 *  A cleared output column falls to NULL in the stack (the backend defaults it). */
export function setUnionColumn(
  unions: DatasetModelUnion[],
  idx: number,
  output: string,
  field: string,
): DatasetModelUnion[] {
  return unions.map((u, i) => {
    if (i !== idx) return u;
    const rest = u.columns.filter((c) => c.output !== output);
    return { ...u, columns: field ? [...rest, { output, field }] : rest };
  });
}

/** Read the persisted UNFOLD entries back off a `config.model` object (the inverse of
 *  the `unfold` piece of {@link buildModelSpec}). */
export function unfoldFromModel(fed: Record<string, unknown> | undefined): DatasetModelUnfold[] {
  const u = (fed ?? {})["unfold"];
  return Array.isArray(u) ? (u as DatasetModelUnfold[]) : [];
}

/** Append an unfold entry for a column (keep_empty defaults true), de-duping by field. */
export function addUnfold(unfold: DatasetModelUnfold[], field: string): DatasetModelUnfold[] {
  const f = field.trim();
  if (!f || unfold.some((u) => u.field === f)) return unfold;
  return [...unfold, { field: f, keep_empty: true }];
}

/** Remove an unfold entry by index. */
export function removeUnfold(unfold: DatasetModelUnfold[], idx: number): DatasetModelUnfold[] {
  return unfold.filter((_, i) => i !== idx);
}

/** Flip an unfold entry's `keep_empty` (keep an empty-array row once vs drop it). */
export function setUnfoldKeepEmpty(
  unfold: DatasetModelUnfold[],
  idx: number,
  keep: boolean,
): DatasetModelUnfold[] {
  return unfold.map((u, i) => (i === idx ? { ...u, keep_empty: keep } : u));
}

/** Read the persisted shape back off a `config.model` object (the inverse of
 *  {@link buildModelSpec}), with safe defaults for every field. */
export function shapeFromModel(fed: Record<string, unknown> | undefined): QbShape {
  const f = (fed ?? {}) as Record<string, unknown>;
  const arr = <T,>(k: string): T[] => (Array.isArray(f[k]) ? (f[k] as T[]) : []);
  return {
    select: arr<QbFieldRef>("select"),
    group_by: arr<QbFieldRef>("group_by"),
    aggregations: arr<QbAgg>("aggregations"),
    filters: arr<QbFilter>("filters"),
    match: f.match === "any" ? "any" : "all",
    order_by: arr<QbSort>("order_by"),
    search: typeof f.search === "string" ? f.search : "",
  };
}

// ---------------------------------------------------------------------------

/** Drop a whole join (its node + all its edges). Any OTHER join that referenced it
 *  as a `left_dataset` would dangle — those ON pairs are pruned too, so the saved
 *  spec never references a removed relation (the engine would reject it). */
export function removeJoin(
  joins: DatasetModelJoin[],
  joinIndex: number,
): DatasetModelJoin[] {
  const removedName = joins[joinIndex]?.dataset;
  return joins
    .filter((_, i) => i !== joinIndex)
    .map((j) =>
      removedName
        ? { ...j, on: (j.on ?? []).filter((o) => (o.left_dataset ?? "") !== removedName) }
        : j,
    );
}
